import { useEffect } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
import { createLowlight, common } from 'lowlight'

const lowlight = createLowlight(common)

interface WysiwygEditorProps {
  value: string
  onChange: (html: string) => void
}

type FormatCommand =
  | 'bold' | 'italic' | 'strike' | 'code'
  | 'h2' | 'h3' | 'bulletList' | 'orderedList'
  | 'blockquote' | 'codeBlock' | 'horizontalRule'

const TOOLBAR_GROUPS: { cmd: FormatCommand; label: string; title: string }[][] = [
  [
    { cmd: 'bold',         label: 'B',   title: 'Bold' },
    { cmd: 'italic',       label: 'I',   title: 'Italic' },
    { cmd: 'strike',       label: 'S̶',   title: 'Strikethrough' },
    { cmd: 'code',         label: '</>',  title: 'Inline code' },
  ],
  [
    { cmd: 'h2',           label: 'H2',  title: 'Heading 2' },
    { cmd: 'h3',           label: 'H3',  title: 'Heading 3' },
    { cmd: 'bulletList',   label: '•—',  title: 'Bullet list' },
    { cmd: 'orderedList',  label: '1.',  title: 'Ordered list' },
  ],
  [
    { cmd: 'blockquote',   label: '❝',   title: 'Blockquote' },
    { cmd: 'codeBlock',    label: '{ }', title: 'Code block' },
    { cmd: 'horizontalRule', label: '—', title: 'Horizontal rule' },
  ],
]

export function WysiwygEditor({ value, onChange }: WysiwygEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: false }),
      CodeBlockLowlight.configure({ lowlight }),
    ],
    content: value,
    onUpdate({ editor: ed }) {
      onChange(ed.getHTML())
    },
    editorProps: {
      attributes: { 'data-placeholder': 'Start writing your post body here…' },
    },
  })

  // Sync external value changes (e.g. HTML tab edits)
  useEffect(() => {
    if (!editor) return
    if (editor.getHTML() !== value) {
      editor.commands.setContent(value, false)
    }
  }, [value, editor])

  const run = (cmd: FormatCommand) => {
    if (!editor) return
    const chain = editor.chain().focus()
    switch (cmd) {
      case 'bold':          chain.toggleBold().run(); break
      case 'italic':        chain.toggleItalic().run(); break
      case 'strike':        chain.toggleStrike().run(); break
      case 'code':          chain.toggleCode().run(); break
      case 'h2':            chain.toggleHeading({ level: 2 }).run(); break
      case 'h3':            chain.toggleHeading({ level: 3 }).run(); break
      case 'bulletList':    chain.toggleBulletList().run(); break
      case 'orderedList':   chain.toggleOrderedList().run(); break
      case 'blockquote':    chain.toggleBlockquote().run(); break
      case 'codeBlock':     chain.toggleCodeBlock().run(); break
      case 'horizontalRule': chain.setHorizontalRule().run(); break
    }
  }

  return (
    <>
      <div className="wysiwyg">
        <div className="wysiwyg-toolbar">
          {TOOLBAR_GROUPS.map((group, gi) => (
            <div key={gi} className="wysiwyg-toolbar-group">
              {group.map(({ cmd, label, title }) => (
                <button
                  key={cmd}
                  type="button"
                  className="wysiwyg-btn"
                  title={title}
                  onClick={() => run(cmd)}
                >
                  {label}
                </button>
              ))}
            </div>
          ))}
        </div>
        <EditorContent editor={editor} className="wysiwyg-editor-area" />
      </div>

      <style>{`
        .wysiwyg {
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          overflow: hidden;
        }
        .wysiwyg:focus-within { border-color: var(--accent); }
        .wysiwyg-toolbar {
          display: flex;
          flex-wrap: wrap;
          gap: 1px;
          padding: 8px 10px;
          background: var(--bg-surface);
          border-bottom: 1px solid var(--border);
        }
        .wysiwyg-toolbar-group {
          display: flex;
          gap: 1px;
          padding-right: 8px;
          margin-right: 8px;
          border-right: 1px solid var(--border);
        }
        .wysiwyg-toolbar-group:last-child { border-right: none; margin-right: 0; padding-right: 0; }
        .wysiwyg-btn {
          padding: 5px 9px;
          border-radius: var(--radius-sm);
          font-size: 0.78rem;
          font-family: var(--font-mono);
          font-weight: 600;
          color: var(--text-secondary);
          transition: background 0.12s, color 0.12s;
        }
        .wysiwyg-btn:hover { background: var(--bg-hover); color: var(--text-primary); }
        .wysiwyg-editor-area { min-height: 340px; }
      `}</style>
    </>
  )
}
